import React, { useEffect, useRef, useState } from 'react';
import { C, MONO, SANS } from './tokens';
import { CALCULATOR_SHEETS, CALCULATOR_FAMILIES, CalculatorEvidence, CalculatorProjection,
  EvidenceHistory, RawEvidenceRecord, fetchCalculatorEvidence, fetchCalculatorHistory } from '../../lib/mackCalculatorEvidence';
import { useCalculatorRetainedRead } from '../../lib/useCalculatorRetainedRead';

// Existing unrelated tabs retain their prior imports; this evidence UI consumes none of them.
export { MACK_CALC_TIERS, MACK_CALC_ENVIRONMENTS, MACK_CALC_SHEETS } from './calculatorLegacyCompatibility';
export type { MackBlockKey } from './blocks';

const box: React.CSSProperties = {border: `1px solid ${C.cardBorder}`, background: C.card,
  padding: 16, borderRadius: 7, marginBottom: 12};
const muted: React.CSSProperties = {color: C.textSub, fontSize: 12};

function RawRecordEvidence({record: r}: {record: RawEvidenceRecord}) {
  return <div style={{...muted, marginTop: 8}}>
      {r.article_identity ? <><a href={r.canonical_url} target="_blank" rel="noreferrer" style={{color:C.amber}}>{r.title}</a>
        <div>{r.publisher} · {r.language} · Published: {r.publication_at} · Retrieved: {r.retrieved_at}</div>
        <div>Currency associations: {r.currency_associations?.join(', ')} · Queries: {r.query_ids?.join(', ')}</div>
        <div>{r.article_identity} · {r.source_access_state} · Raw metadata; Formula Pass 2 pending</div>
        {r.source_feed_urls?.map(url => <div key={url}>Publisher feed: {url}</div>)}</>
        : <><div>{r.instrument_label || r.instrument}: Long {r.long_percentage}% · Short {r.short_percentage}%</div>
          <div>{r.denominator}</div>
          <div>Observed by Mack: {r.observed_at} · Retrieved: {r.retrieved_at}</div>
          <div>Continuous source snapshot at retrieval · {r.reference_period}</div>
          <div>Provider update: Not exposed · {r.provider_update_state}</div>
          <div>Collection window: {r.collection_window_id} · Mack retrieval is not IG publication time</div>
          <div>{r.source_access_state} · Formula Pass 2 pending</div></>}
  </div>;
}

function EvidenceRow({field, authToken}: {field: CalculatorEvidence; authToken?: string | null}) {
  const [history, setHistory] = useState<EvidenceHistory | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  useEffect(() => { setHistory(null); setOffset(0); setError(null); setLoading(false);
    return () => { generation.current++; }; }, [field.field_id, field.history_origin, authToken]);
  async function load(offset = 0) {
    const request = ++generation.current;
    setLoading(true); setError(null);
    try {
      const next = await fetchCalculatorHistory(field, authToken, offset);
      if (generation.current === request) { setHistory(next); setOffset(offset); }
    } catch { if (generation.current === request) setError('History unavailable; the last confirmed page remains visible.'); }
    finally { if (generation.current === request) setLoading(false); }
  }
  return <div style={{borderTop: `1px solid ${C.rule}`, paddingTop: 10, marginTop: 10}} data-evidence-id={field.field_id}>
    <div style={{display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline'}}>
      <strong>{field.label}</strong>
      <span style={{fontFamily: MONO}}>{field.value === null ? (field.raw_records?.length ? `${field.raw_records.length} raw evidence records` : field.raw_record ? 'Raw instrument evidence' : 'Unavailable') : String(field.value)}</span>
      <span style={muted}>{field.unit || 'Unit unavailable'} · {field.state}</span>
    </div>
    <div style={muted}>Reference: {field.reference_period || 'Unavailable'} · {field.record_kind === 'retail_positioning'
      ? 'Continuous source snapshots; provider update timestamp not exposed'
      : `Publication: ${field.publication_at || 'Not exposed by source'}`}</div>
    {field.instrument_map && <div style={muted}>Instrument evidence (provider account population; no currency percentage):
      {field.instrument_map.map((r, i) => <div key={`${r.field_id}:${r.role}:${i}`}>{r.instrument} · {r.role} currency role · {r.field_id}
        {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" style={{color:C.amber,marginLeft:8}}>Instrument source</a>}</div>)}
    </div>}
    {field.instrument_coverage && <div style={muted}>Instrument coverage:
      {Object.entries(field.instrument_coverage).map(([identity, state]) => <div key={identity}>{identity}: {state}</div>)}
    </div>}
    {field.constituent_coverage && <div style={muted}>Constituent coverage:
      {Object.entries(field.constituent_coverage).map(([currency, state]) => <div key={currency}>{currency}: {state}</div>)}
      <div>Aggregation pending Formula Pass 2</div>
    </div>}
    {(field.raw_records || (field.raw_record ? [field.raw_record] : [])).map((r, i) =>
      <RawRecordEvidence key={r.article_identity || `${r.instrument}:${i}`} record={r}/>)}
    {field.source_edition_at && <div style={muted}>Source table edition: {field.source_edition_at} · original historical publication remains unproved</div>}
    {field.scheduled_at && <div style={muted}>Scheduled: {field.scheduled_at} · publication remains separate</div>}
    <div style={muted}>Acquired: {field.retrieved_at || 'No production acquisition'} · {field.publication_state}</div>
    <div style={muted}>Origin: {field.origin} · {field.publisher || 'Publisher unavailable'} · {field.frequency || 'Frequency unavailable'}</div>
    <div style={{...muted, wordBreak: 'break-word'}}>Series/selector: {typeof field.series_id === 'string' ? field.series_id : JSON.stringify(field.series_id)}</div>
    <div style={{...muted, wordBreak: 'break-word'}}>{field.field_id}</div>
    {field.source_url && <a style={{color: C.amber, fontSize: 12}} href={field.source_url} target="_blank" rel="noreferrer">Exact source</a>}
    {field.source_urls?.filter(url => url !== field.source_url).map(url => <div key={url} style={muted}><a href={url} target="_blank" rel="noreferrer" style={{color:C.amber}}>Exact source: {url}</a></div>)}
    {field.reason && !field.current_eligible && <div style={{...muted,color:C.amber}}>{field.reason}</div>}
    <button type="button" disabled={loading} onClick={() => load()} style={{marginLeft: 12, cursor: 'pointer'}}>
      {loading ? 'Loading history…' : `History (${field.history_count})`}
    </button>
    <span style={{...muted, marginLeft: 8}}>{field.history_state}</span>
    {error && <div role="alert" style={muted}>{error}</div>}
    {history && <div style={{maxHeight: 240, overflow: 'auto', marginTop: 8}}>
      <div style={{...muted,overflowWrap:'anywhere'}}>History identity: {history.field_id} · {history.origin}</div>
      <table style={{fontSize: 12, width: '100%', textAlign: 'left'}}>
        <thead><tr><th>Reference</th><th>Source evidence</th><th>Unit</th><th>Publication</th><th>Source edition / update</th><th>Observation</th><th>Retrieval</th><th>Receipt / recorded</th><th>Window</th><th>Vintage</th></tr></thead>
        <tbody>{history.rows.map((r, i) => <tr key={String(r.material_hash || r.vintage_id || i)}>
          <td>{String(r.reference_period || 'Unavailable')}</td>
          <td>{r.raw_record ? <RawRecordEvidence record={r.raw_record as RawEvidenceRecord}/>
            : r.source_native_value === null || r.source_native_value === undefined ? 'Unavailable' : String(r.source_native_value)}</td>
          <td>{String(r.unit || 'Unavailable')}</td><td>{String(r.publication_at || 'Not exposed')}</td>
          <td>{String(r.source_edition_at || 'Not exposed')}<br />{String(r.provider_update_at ?? r.source_update_at ?? 'Not exposed')}</td>
          <td>{String(r.observed_at || 'Not exposed')}</td><td>{String(r.retrieved_at || 'Not exposed')}</td>
          <td>{String(r.received_at || 'Not exposed')}<br />{String(r.recorded_at || 'Not exposed')}</td>
          <td>{String(r.collection_window_id || 'Not applicable')}</td>
          <td style={{wordBreak:'break-all'}}>{String(r.vintage_id || r.material_hash || 'Unavailable')}</td>
        </tr>)}</tbody>
      </table>
      <button type="button" disabled={loading || offset === 0} onClick={() => load(Math.max(0, offset - 30))}>Previous history page</button>
      {history.next_offset !== null && <button type="button" disabled={loading} onClick={() => load(history.next_offset as number)}>Load older history</button>}
      <details><summary>Complete history, vintages and persistence lineage</summary><pre style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{JSON.stringify(history,null,2)}</pre></details>
    </div>}
  </div>;
}

export function CalculatorEvidenceView({projection, authToken}: {projection: CalculatorProjection | null; authToken?: string | null}) {
  return <>{CALCULATOR_SHEETS.map(sheet => <section key={sheet} aria-label={`${sheet} Calculator evidence`}>
    <h2 style={{color: C.amber, fontFamily: MONO, fontSize: 16, marginTop: 26}}>{sheet} · Evidence</h2>
    {sheet === 'BLOC' && <p style={muted}>Aggregation pending Formula Pass 2. Individual constituent evidence is retained.</p>}
    {CALCULATOR_FAMILIES.map(key => {
      const identity = `mack.criterion.${sheet}.${key}`;
      const slot = projection?.slots.find(r => r.field_id === identity);
      return <article key={identity} style={box} data-criterion-id={identity}>
        <h3 style={{margin: '0 0 6px', fontSize: 14}}>{slot?.label || key.replace(/_/g,' ')}</h3>
        <div style={{fontSize: 12,color:C.amber}}>{slot?.formula_state === 'FORMULA_PENDING_INPUT_UNAVAILABLE'
          ? 'Formula pending — required input unavailable' : 'Formula Pass 2 pending'}</div>
        {!slot && <p style={muted}>Evidence unavailable — backend projection has not been read.</p>}
        {slot?.evidence.map(field => <EvidenceRow key={`${field.field_id}:${field.retrieved_at}:${field.history_count}`} field={field} authToken={authToken}/>)}
        {slot?.unavailable_evidence.map(gap => <div key={gap.identity} style={{...muted,marginTop:10}}>
          <strong>{gap.state}</strong> · {gap.identity}<div>{gap.reason}</div>
        </div>)}
        {slot && slot.supporting_evidence.length > 0 && <details style={{marginTop: 10}}>
          <summary style={muted}>Supporting raw evidence and upstream pending inputs</summary>
          {slot.supporting_evidence.map(field => <EvidenceRow key={`${field.field_id}:${field.retrieved_at}:${field.history_count}`} field={field} authToken={authToken}/>)}
        </details>}
        {slot?.upstream_formula_states.map(r => <div key={r.field_id} style={{...muted,marginTop:6}}>
          {r.field_id} · Inputs Formula Pass 2 pending; raw leaves remain visible
        </div>)}
        {slot && <details style={{marginTop:10}}><summary style={muted}>Release evidence and downstream destinations</summary>
          {slot.calendar_bindings.map(r => <div key={`${identity}:${r.event_uuid}`} style={muted}>
            Calendar: {r.source_label} · {r.event_uuid} · trigger/context evidence
          </div>)}
          {slot.calendar_evidence.map(field => <EvidenceRow key={field.field_id} field={field} authToken={authToken}/>)}
          <div style={muted}>{slot.workbook_destination.UI} · Future block: {slot.workbook_destination.named_block_input}</div>
          <div style={muted}>{slot.workbook_destination.BF_outputs}</div>
          {Object.entries(slot.downstream).map(([tab,destination]) => <div key={tab} style={muted}>{tab}: {destination}</div>)}
        </details>}
      </article>;
    })}
  </section>)}</>;
}

export function CalculatorTab({authToken}: {authToken?: string | null}) {
  const { value: projection, error } = useCalculatorRetainedRead(fetchCalculatorEvidence, authToken);
  return <div style={{color:C.text, fontFamily:SANS, padding: 20}}>
    <h1 style={{color:C.amber,fontSize:20,marginTop:0}}>Calculator · Evidence</h1>
    <p style={muted}>84 criteria across six sheets. Scores await Formula Pass 2.</p>
    {projection && <p style={muted}>Direct-source instructions: {projection.T1_installation.state}</p>}
    {error && <p role="alert" style={{color:C.amber}}>Calculator evidence is currently unavailable{projection ? '; previously confirmed evidence remains visible' : ''}</p>}
    <CalculatorEvidenceView projection={projection} authToken={authToken}/>
  </div>;
}
